import { useEffect, useState, useSyncExternalStore, type FC } from 'react';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { FeatureVersionCard } from './FeatureVersionCard';
import { FeatureInvitationModal } from './FeatureInvitationModal';
import { ComparePeekBar } from './ComparePeekBar';
import { EvacuatedNotice } from './EvacuatedNotice';
import { GaAnnouncementBanner } from './GaAnnouncementBanner';
import { RevertFeedbackPrompt } from './RevertFeedbackPrompt';
import { startPreview } from '@utils/featureRollout/preview';
import { getActiveCommerceId } from '@utils/featureRollout/cache';
import { isRoleRejected, subscribeRoleRejection } from '@utils/featureRollout/roleRejection';
import {
  clearRevertFeedback,
  isRevertFeedbackRequested,
  subscribeRevertFeedback,
} from '@utils/featureRollout/revertFeedback';
import { MENU_FEATURE_KEY } from '@utils/featureRollout/constants';
import type { RevertFeedbackReason } from '@utils/featureRollout/strings';

// Conector del framework de rollout para `menu-v2`: cablea el hook + la identidad root
// con la card reutilizable y el modal contextual de primera vez. Se monta en el shell del
// menú (sobre la página resuelta), tanto en V1 (control) como en V2 (treatment).
//
// CADENCIA SERVER-SIDE (contrato §8): el FE ya NO recalcula con menu_state. OBEDECE
// `inviteSurface` que deriva el backend (cooldown 21d→45d→sticky, cap 3, snooze, next_eligible).
// La card/modal son el canal PUSH y se gobiernan EXCLUSIVAMENTE por inviteSurface:
//   - 'modal' → modal contextual + card (primera vez, never_tried).
//   - 'card'  → solo la card discreta (recordatorio dentro del cap).
//   - 'none'  → no invitar PUSH (sticky | GA | evacuado | snooze | next_eligible futuro).
//
// DEDUP del CTA en control: el chip PULL (MenuVersionStatusChip) y la card PUSH NO se solapan
// porque el chip solo aparece con inviteSurface==='none'. Mientras la card está activa
// (modal/card) ella muestra el único CTA; al descartar/snoozear (inviteSurface→'none') el chip
// reaparece como entrada discreta. Aquí también se RENDERIZA el prompt de feedback al revertir
// (pedido por el chip de status vía signal), que debe sobrevivir al desmontaje de la página V2.
//
// El switch durable lo dispara el root EFECTIVO (isRoot del FE y SIN rechazo de rol del backend
// esta sesión); no-root o rol-rechazado → preview per-usuario (card y modal lo manejan).

const MenuVersionInvitation: FC = () => {
  const {
    variant,
    canRevert,
    gaForced,
    inviteSurface,
    operatorEvacuated,
    evacReason,
    compareActive,
    switchTo,
    revert,
    optOut,
    snooze,
    markInvitationShown,
    submitRevertFeedback,
  } = useFeatureVersion(MENU_FEATURE_KEY);
  const { isRoot } = useSubscription();

  // Rechazo de rol del backend (REQUIRES_ROOT) en esta sesión: si está, el usuario NO es root
  // efectivo aunque el FE lo crea → la card/modal ofrecen preview en vez del CTA durable (rompe
  // el loop). Mismo criterio que MenuVersionStatusChip.
  const roleRejected = useSyncExternalStore(
    (cb) => subscribeRoleRejection(MENU_FEATURE_KEY, cb),
    () => isRoleRejected(getActiveCommerceId(), MENU_FEATURE_KEY),
    () => isRoleRejected(getActiveCommerceId(), MENU_FEATURE_KEY),
  );
  const effectiveRoot = Boolean(isRoot) && !roleRejected;

  // Prompt de feedback al revertir (D1): el chip de status lo PIDE vía signal (al revertir, su
  // página treatment se desmonta); este componente vive en el shell y persiste el flip, así que
  // es quien lo RENDERIZA. Omitible y fire-and-forget; nunca bloquea ni revierte el cambio.
  const feedbackRequested = useSyncExternalStore(
    (cb) => subscribeRevertFeedback(MENU_FEATURE_KEY, cb),
    () => isRevertFeedbackRequested(MENU_FEATURE_KEY),
    () => isRevertFeedbackRequested(MENU_FEATURE_KEY),
  );
  const onFeedbackSubmit = (reason: RevertFeedbackReason, text: string) => {
    submitRevertFeedback(reason, text, 'revert_feedback');
    clearRevertFeedback(MENU_FEATURE_KEY);
  };
  const onFeedbackSkip = () => clearRevertFeedback(MENU_FEATURE_KEY);

  // Item E#4 — el CTA decide por ROL EN RENDER. Mientras isRoot NO esté resuelto (undefined antes
  // de /v1/auth/account-details) NO mostramos un CTA potencialmente equivocado (un root vería el
  // CTA de preview por el breve instante en que isRoot=false-por-defecto). En vez de un spinner de
  // PANTALLA ENTERA, simplemente NO montamos la card/modal hasta resolver: el menú ya se ve.
  const roleResolved = isRoot !== undefined;

  // Superficies derivadas del backend (no heurística local):
  //   showCard = inviteSurface ∈ {modal, card}; showModal = inviteSurface === 'modal'.
  // En treatment NO se invita (la card de invitación es solo para control).
  const showCard = roleResolved && (inviteSurface === 'modal' || inviteSurface === 'card') && variant !== 'treatment';
  const canOpenModal = roleResolved && inviteSurface === 'modal' && variant !== 'treatment';

  // El modal se abre una vez por montaje del shell si el backend lo indica (inviteSurface=modal).
  // Decisión SÍNCRONA (el invite_surface ya viaja en el cache hidratado) → sin parpadeo.
  const [showModal, setShowModal] = useState(() => canOpenModal);

  // inviteSurface puede llegar después de hidratar. Si pasa a 'modal', abrimos una sola vez.
  const openedRef = useState(() => ({ done: false }))[0];
  useEffect(() => {
    if (openedRef.done) return;
    if (canOpenModal) {
      openedRef.done = true;
      setShowModal(true);
    }
  }, [canOpenModal, openedRef]);

  const closeModal = () => setShowModal(false);
  // "No volver a preguntar" → opt_out (el backend la vuelve sticky).
  const neverAsk = () => {
    optOut('menu_modal');
    setShowModal(false);
  };
  // "Recordármelo más tarde" → snooze 7 días (el backend suprime hasta entonces).
  const remindLater = () => {
    snooze(7, 'menu_modal');
    setShowModal(false);
  };
  const acceptFromModal = () => {
    switchTo('treatment', 'menu_modal');
    setShowModal(false);
  };
  // NO-ROOT: "echa un vistazo" → vista previa per-usuario (no toca el comercio ni el switch
  // durable). El gate del menú lee isPreviewActive y renderiza V2 solo para este usuario/tab.
  const previewFromModal = () => {
    startPreview(MENU_FEATURE_KEY);
    setShowModal(false);
  };
  const previewFromCard = () => startPreview(MENU_FEATURE_KEY);

  return (
    <>
      {/* Prompt de feedback al revertir (D1). Pedido por el chip de status vía signal; se renderiza
          aquí (shell persistente) porque al revertir la página treatment —y su chip— se desmonta.
          OVERLAY fijo (mismo patrón que FeatureInvitationModal): NO va en el flujo del documento
          para no EMPUJAR el contenido del menú; flota centrado sobre la página. Backdrop-click =
          omitir (fire-and-forget, no bloquea). */}
      {feedbackRequested && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17,26,34,.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 1000,
            fontFamily: 'inherit',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onFeedbackSkip();
          }}
        >
          <div style={{ width: '100%', maxWidth: 420 }}>
            <RevertFeedbackPrompt onSubmit={onFeedbackSubmit} onSkip={onFeedbackSkip} />
          </div>
        </div>
      )}

      {/* Ramp-down: el operador nos regresó a la anterior. No invitamos; copy REASON-AWARE
          (temporal vs descontinuado) para que el comercio entienda qué pasó. */}
      {operatorEvacuated && (
        <div style={{ marginBottom: 12 }}>
          <EvacuatedNotice reason={evacReason} />
        </div>
      )}

      {/* Anuncio GA (C2): el comercio ya está graduado (la versión nueva es la oficial). Banner
          descartable tipo "nueva funcionalidad", no intrusivo. Se auto-oculta si ya fue descartado.
          No se muestra durante un peek (la barra de comparación tiene prioridad). */}
      {gaForced && !operatorEvacuated && !compareActive && (
        <div style={{ marginBottom: 12 }}>
          <GaAnnouncementBanner featureKey={MENU_FEATURE_KEY} />
        </div>
      )}

      {/* Barra sticky de comparación: visible solo durante un peek (compare_active). Vive aquí
          (shell del menú) porque durante el peek la variante resuelta es V1 (control). */}
      {compareActive && (
        <ComparePeekBar onReturn={() => switchTo('treatment', 'compare_return')} isRoot={effectiveRoot} />
      )}

      {/* INVITACIÓN (control): la card solo si el backend la habilita (modal/card) y no estamos
          evacuados ni comparando. En treatment el indicador vive como CHIP (MenuVersionStatusChip). */}
      {showCard && !operatorEvacuated && !compareActive && (
        <div style={{ marginBottom: 12 }}>
          <FeatureVersionCard
            variant={variant}
            canRevert={canRevert}
            gaForced={gaForced}
            isRoot={effectiveRoot}
            onTry={() => switchTo('treatment', 'menu_shell')}
            onPreview={previewFromCard}
            onRevert={() => revert('revert_commit')}
            onShown={() => markInvitationShown('menu_shell')}
          />
        </div>
      )}

      {showModal && !operatorEvacuated && (
        <FeatureInvitationModal
          isRoot={effectiveRoot}
          onTry={acceptFromModal}
          onPreview={previewFromModal}
          onDismiss={closeModal}
          onRemindLater={remindLater}
          onNeverAsk={neverAsk}
          onShown={() => markInvitationShown('menu_modal')}
        />
      )}
    </>
  );
};

export default MenuVersionInvitation;
